/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.22.0
 */

import { useEffect } from 'react';
import type { FC } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { ERequestStatus } from '@/main/types/globalTypes';
import { stringFormatParams } from '@/main/utils/stringFormat';
import { icons } from '@assets/index';
import { LinkBlock } from '@components/common';
import { ImgStyled } from '@components/common/Block/Img';
import type { TClientId } from '@pages/PageAccountInfo/types';

import { makeSelectEdmOperatorConnectionTranslation, makeSelectTranslationCommon } from '../../internationalization/intSelectors';
import { Loader } from '../../loader/components/Loader';
import Modal from '../../modal/components/Modal';
import { ModalTitleStyled, ModalTitleTextStyled } from '../../modal/components/styled/ModalStyled';
import { modalSetStatusAction } from '../../modal/modalActions';
import { EModalStatus } from '../../modal/types/modalTypes';
import ButtonServiceUserDenied from '../../role/components/ButtonServiceUserDenied';
import { useCancelEdmConnectionMutation } from '../store';
import type { TEdmConnectionId } from '../types';
import { EdmRequestStatusModal } from './EdmRequestStatusModal';

export interface IProps {
    clientId: TClientId;
    edmConnectionId: TEdmConnectionId;
    operator: string;
    onCloseModal: () => void;
};

export const EdmConnectionCancelModal: FC<IProps> = ({ clientId, edmConnectionId, operator, onCloseModal }) => {
    const dispatch = useDispatch();

    const tCommon = useSelector(makeSelectTranslationCommon());
    const {
        failure: tFailCancelConnection,
        start: tStartCancelConnection,
        success: tSuccessCancelConnection,
    } = useSelector(makeSelectEdmOperatorConnectionTranslation());

    const [cancelEdmConnection, { error, isError, isLoading, isSuccess }] = useCancelEdmConnectionMutation();

    useEffect(() => {
        if (isSuccess) {
            dispatch(modalSetStatusAction({
                status: EModalStatus.success,
            }));
        }
    }, [dispatch, isSuccess]);

    useEffect(() => {
        if (isError) {
            dispatch(modalSetStatusAction({
                status: EModalStatus.failure,
            }))
        }
    }, [dispatch, isError]);

    const handelCancelConnection = () => {
        cancelEdmConnection({ bisAccountId: clientId, edmConnectionId });
    };

    return (
        <Modal
            dataName='edm-connection-cancel-modal'
            style={{ padding: '24px' }}
            width='480px'
        >
            <Modal.Status status={EModalStatus.start}>
                <Modal.Title className='modal-popup-title__base'>
                    <ModalTitleStyled>
                        <ModalTitleTextStyled>
                            {tStartCancelConnection.cancelConnectionTitle}
                        </ModalTitleTextStyled>
                        <ImgStyled height='16px' src={icons.cancelSmallIcon} width='16px' onClick={onCloseModal} />
                    </ModalTitleStyled>
                </Modal.Title>
                <Loader showLoader={isLoading}>
                    <Modal.Text>
                        {stringFormatParams(tStartCancelConnection.cancelConnectionText, { operator })}
                    </Modal.Text>
                    <Modal.Control className='modal-popup-control__base'>
                        <ButtonServiceUserDenied
                            onClick={handelCancelConnection}
                        >
                            {tStartCancelConnection.cancelConnectionButton}
                        </ButtonServiceUserDenied>
                        <LinkBlock onClick={onCloseModal}>
                            {tCommon.modal.close}
                        </LinkBlock>
                    </Modal.Control>
                </Loader>
            </Modal.Status>
            <Modal.Status status={EModalStatus.success}>
                <EdmRequestStatusModal
                    mode={ERequestStatus.success}
                    title={stringFormatParams(tSuccessCancelConnection.cancelConnectionTitle, { operator })}
                    onCloseModal={onCloseModal}
                />
            </Modal.Status>
            <Modal.Status status={EModalStatus.failure}>
                <EdmRequestStatusModal
                    mode={ERequestStatus.fail}
                    requestError={error}
                    title={tFailCancelConnection.cancelConnectionTitle}
                />
            </Modal.Status>
        </Modal>
    );
};
